/**
 * THE UPLOAD LABEL — whose name is on a fact, and how it gets there.
 *
 * WHY THIS IS A MODULE AND NOT THREE LINES INSIDE THE COMPONENT. The label an
 * owner sees above the upload button becomes `source_doc` on every fact the file
 * produces, permanently, and it is the only answer the corpus will ever have to
 * "where did this come from?". A label that does not match the file is not a
 * cosmetic bug — it is a false entry in the audit trail, written in good faith,
 * indistinguishable afterwards from a true one.
 *
 * The rules below are genuinely fiddly (three inputs, two of which are about
 * INTENT rather than content), and the suite has no way to exercise React state
 * — every one of its forty files is a worker or a pure module. So the logic
 * lives here, where it can be tested, and the component holds only what it must:
 * the current values and the wiring. Same reasoning, and the same shape, as
 * fact-scope, counters-format and decay-format.
 *
 * THE DEFECT THIS EXISTS TO CLOSE (found in production, 31 Aug 2026). The first
 * version prefilled the label from the filename ONLY when the label was empty,
 * so that picking a second file could never overwrite something the owner had
 * carefully typed. That intent was right. What it missed is that a REFUSED
 * upload leaves its filename sitting in the box: the file is rejected, the label
 * is not cleared, and the next file inherits the previous file's name in
 * silence. A scan was refused, a real document was picked, and the label still
 * read `scanned-test.pdf` — nine facts were one keystroke away from being
 * labelled with a document they did not come from.
 *
 * THE FIX IS TO TRACK WHERE THE LABEL CAME FROM, not to clear more aggressively.
 * Clearing the label on every refusal would also throw away a label the owner
 * typed himself, which is the thing the original rule was protecting. A label
 * this code supplied belongs to the file it was taken from and must follow it; a
 * label the OWNER typed belongs to him and must survive. Those are different
 * facts about the same string, and no amount of inspecting the string reveals
 * which one it is — so the origin is carried explicitly.
 */

export interface LabelState {
  /** What is currently in the box. */
  label: string
  /**
   * True when this code put it there by copying a filename; false when the
   * owner typed or edited it. The whole fix turns on this distinction.
   */
  isAuto: boolean
}

export const EMPTY_LABEL: LabelState = { label: '', isAuto: false }

/**
 * A file has just been chosen.
 *
 * An auto-supplied label is REPLACED, because it was only ever a copy of the
 * previous file's name and that file is no longer the subject. An owner-typed
 * label is KEPT, because he meant it — and if it was right for the document he
 * was uploading a moment ago, it is probably still right for the corrected
 * version of it he is uploading now.
 */
export function labelAfterFileChosen(current: LabelState, fileName: string | null): LabelState {
  if (fileName === null) return current
  if (current.isAuto || current.label.trim() === '') {
    return { label: fileName, isAuto: true }
  }
  return current
}

/**
 * The owner has edited the box. From this point the label is his, and no future
 * file selection may overwrite it — including a selection that happens after a
 * refusal.
 */
export function labelAfterOwnerEdit(next: string): LabelState {
  return { label: next, isAuto: false }
}

/**
 * The upload succeeded. Both the file and the label clear, because — unlike a
 * paste, where the next paste is usually page two of the same document — the
 * next upload is a different document entirely.
 */
export function labelAfterSuccess(): LabelState {
  return EMPTY_LABEL
}

/**
 * The upload was refused.
 *
 * THE LABEL IS NOT CLEARED, and that is deliberate: the owner may want to fix
 * the filename, or pick a corrected file and keep the wording he typed. What
 * matters is that an AUTO label stays marked as auto, so the next file replaces
 * it rather than inheriting it. Leaving the state untouched is therefore the
 * correct behaviour — but only because `isAuto` survives with it. Before that
 * flag existed, doing nothing here is exactly what caused the defect.
 */
export function labelAfterRefusal(current: LabelState): LabelState {
  return current
}

/**
 * Whether the form may be submitted. A file and a non-empty label, both — the
 * label is not optional, because a fact with no provenance is a fact that cannot
 * be audited later.
 */
export function canSubmitUpload(state: LabelState, hasFile: boolean, busy: boolean): boolean {
  return hasFile && state.label.trim().length > 0 && !busy
}

/**
 * WHY THE BUTTON IS GREYED, IN WORDS (live walk, 29 Sep 2026).
 *
 * The owner pasted his text, chose a drawer, and sat looking at a greyed
 * "Read this document" with nothing saying why: the name box was empty, and its
 * faded example looked like a filled-in answer. A disabled button that does not
 * say what it is waiting for reads as broken.
 *
 * Returns the ONE next thing to do, in the order the card is filled in, or null
 * when nothing is missing (or while a read is running, when the button says
 * "Reading…" and needs no excuse). The screen shows it only while the button is
 * disabled, so a disagreement with the screen's own check can never put a
 * "do this first" beside a button that works.
 */
export function submitBlocker(input: {
  mode: 'upload' | 'paste'
  hasFile: boolean
  text: string
  label: string
  drawerNeeded: boolean
  drawerChosen: boolean
  busy: boolean
}): string | null {
  if (input.busy) return null
  if (input.mode === 'upload' && !input.hasFile) return 'Choose a file first.'
  if (input.mode === 'paste' && input.text.trim().length === 0) return 'Paste the text first.'
  if (input.label.trim().length === 0) return 'Give it a name first.'
  if (input.drawerNeeded && !input.drawerChosen) return 'Choose a drawer first.'
  return null
}

/**
 * A NAME CARRIED OVER FROM THE LAST PASTE IS SAID OUT LOUD.
 *
 * After a paste only the text clears, deliberately: the next paste is usually
 * page two of the same document, in the same drawer (DocumentIntake.tsx). The
 * cost of that convenience showed on the 29 Sep live walk: the old name sat in
 * the box looking like any other answer, and every fact keeps its name for
 * good, so a DIFFERENT document pasted next would be filed under the wrong
 * "From …" for ever, silently.
 *
 * The decision stands; the silence goes. While the box still holds exactly
 * what the last paste was read under, the card says so. Editing the name (or
 * switching to upload) ends it.
 */
export function carriedNameNotice(
  mode: 'upload' | 'paste',
  carried: string | null,
  label: string,
  drawers: boolean,
): string | null {
  if (mode !== 'paste' || carried === null) return null
  if (label.trim() !== carried) return null
  // Foredesk has no drawers, so it is never told about one.
  const where = drawers ? ', in the same drawer' : ''
  return `Still named “${carried}” from your last paste${where}: right for the next page of the same document. If this is a different document, change the name.`
}
